import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import es from './locales/es.json'
import en from './locales/en.json'
import { setCalendarLocale } from '../utils/calendar-locale'

/**
 * i18next configuration for Tago
 *
 * ABSOLUTE RULE: ZERO hardcoded strings
 * All user-facing text MUST come from locale files
 *
 * Default language: Spanish (es) - El Salvador
 * Fallback language: English (en)
 */

const resources = {
  es: { translation: es },
  en: { translation: en },
}

const SUPPORTED_LOCALES = new Set(['es', 'en'])
const LOCALE_STORAGE_KEY = 'tago_locale'
const LOCAL_LOCALE_GRACE_MS = 30_000

type SupportedLocale = 'es' | 'en'
type LocalLocaleOverride = { locale: SupportedLocale; changedAt: number }
type LocaleGlobal = typeof globalThis & {
  __TAGO_LOCAL_LOCALE_OVERRIDE__?: LocalLocaleOverride | null
}

function getLocaleGlobal() {
  return globalThis as LocaleGlobal
}

function asSupportedLocale(locale: string | null | undefined): SupportedLocale | null {
  return locale === 'es' || locale === 'en' ? locale : null
}

function readStoredLocale(): SupportedLocale | null {
  if (typeof window === 'undefined') return null
  try {
    return asSupportedLocale(window.localStorage.getItem(LOCALE_STORAGE_KEY))
  } catch {
    return null
  }
}

function writeStoredLocale(locale: SupportedLocale) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // Storage can be unavailable in private contexts. The in-memory i18n
    // change still wins for the current session.
  }
}

const defaultLocale = readStoredLocale() ?? 'es'

i18n.use(initReactI18next).init({
  resources,
  lng: defaultLocale,
  fallbackLng: {
    es: ['en'],
    en: ['es'],
    default: [defaultLocale],
  },
  interpolation: {
    escapeValue: false, // React already escapes
  },
  react: {
    useSuspense: false, // Prevents issues with Expo
  },
})

// react-native-calendars keeps its month and weekday names in XDate's global
// locale registry, not in i18next, so it has to be pointed at the same language
// or it silently renders English inside a Spanish screen. It did: the booking
// calendar read "August 2026 / Mon Tue Wed" under "Seleccioná tus fechas".
setCalendarLocale(i18n.language)
i18n.on('languageChanged', setCalendarLocale)

export default i18n

export async function changeLanguageLocally(locale: SupportedLocale) {
  getLocaleGlobal().__TAGO_LOCAL_LOCALE_OVERRIDE__ = { locale, changedAt: Date.now() }
  writeStoredLocale(locale)
  await i18n.changeLanguage(locale)
}

export function shouldApplyProfileLocale(locale: string | null | undefined) {
  if (!locale || !SUPPORTED_LOCALES.has(locale)) return false

  const localeGlobal = getLocaleGlobal()
  const localLocaleOverride = localeGlobal.__TAGO_LOCAL_LOCALE_OVERRIDE__
  const storedLocale = readStoredLocale()

  if (storedLocale && storedLocale !== locale) return false

  if (localLocaleOverride) {
    if (localLocaleOverride.locale === locale) {
      localeGlobal.__TAGO_LOCAL_LOCALE_OVERRIDE__ = null
    } else if (Date.now() - localLocaleOverride.changedAt < LOCAL_LOCALE_GRACE_MS) {
      return false
    }
  }

  return i18n.language !== locale
}
